import { useCallback } from 'react'
import { v4 as uuid } from 'uuid'
import { useNoteProcessor } from '../providers/useNoteProcessor'
import {
  type AddContentInput,
  type RemoveContentInput,
} from '../providers/NoteProcessorProvider'
import { useNotification } from '../providers/NotificationProvider'
import { trackError, trackEvent } from '../lib/analytics'
import { errorIcon } from '../components/Notification'
import {
  type DraggedTask,
  draggedTasksToNotePlan,
} from '../utils/taskDragUtils'
import { useSidebarProvider } from '../contexts/Sidebar'
import { type SelectedDate, selectedDateToKey } from '../contexts/SelectedDate'
import { type NoteType, removeExtension } from '../utils/note'

/**
 * Error types for task move operations
 */
export enum TaskMoveErrorType {
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  EXTRACTION_ERROR = 'EXTRACTION_ERROR',
  PROCESSING_ERROR = 'PROCESSING_ERROR',
  NETWORK_ERROR = 'NETWORK_ERROR',
}

/**
 * Enhanced error class for task move operations
 */
export class TaskMoveError extends Error {
  constructor(
    message: string,
    public type: TaskMoveErrorType,
    public details?: Record<string, string | number | Date | Error>
  ) {
    super(message)
    this.name = 'TaskMoveError'
  }
}

/**
 * Result of a task move operation
 */
export type TaskMoveResult = {
  /** Whether the operation was queued successfully */
  success: boolean
  /** The batch ID for tracking the operation */
  batchId?: string
  /** Any error that occurred during queuing */
  error?: Error
}

/**
 * Hook for robust task moving using the NoteProcessorProvider batch system
 *
 * This hook provides a transactional approach to moving tasks between notes.
 * It always performs a true move operation (add to target + remove from source).
 * Operations are queued for background processing.
 *
 * Key features:
 * - Atomic move operations (add + remove as single transaction)
 * - Strict validation - fails if source note information is missing
 * - User feedback via notifications
 * - Background processing via NoteProcessorProvider
 */
export function useTaskMove() {
  const { addContent, removeContent } = useNoteProcessor()
  const { showNotification } = useNotification()
  const { noteKey } = useSidebarProvider()

  /**
   * Moves tasks from source to target date (always a true move operation)
   * Queues operations for background processing and returns immediately
   */
  const moveTaskToDate = useCallback(
    (
      tasks: DraggedTask[],
      targetDate: SelectedDate,
      targetNoteType: NoteType,
      teamspaceId?: string
    ): TaskMoveResult => {
      try {
        // Validate input
        if (tasks.length === 0) {
          throw new TaskMoveError(
            'No tasks provided for move operation',
            TaskMoveErrorType.VALIDATION_ERROR,
            { taskCount: tasks.length }
          )
        }

        // Validate that we have valid task content
        const taskLines = draggedTasksToNotePlan(tasks)
        if (taskLines.length === 0) {
          throw new TaskMoveError(
            'No valid task content found',
            TaskMoveErrorType.EXTRACTION_ERROR,
            { originalTaskCount: tasks.length }
          )
        }

        // Generate a unique batch ID for this transaction
        const batchId = uuid()

        const targetFilename = selectedDateToKey(targetDate)

        // Track the operation start
        trackEvent('web.useTaskMove.start', {
          taskCount: tasks.length,
          targetFilename,
          targetNoteType,
          batchId,
        })

        // Prepare add operation for target note
        const addOperations: AddContentInput[] = [
          {
            type: 'add',
            dateString: targetFilename,
            content: taskLines.join('\n'),
          },
        ]

        // Prepare remove operations for source note

        // Extract task content lines for removal - use reconstructTaskLine for accuracy
        const taskContentsToRemove: string[] = []

        for (const task of tasks) {
          // Add ALL lines from the content to ensure children are removed too
          const contentLines = task.content.split('\n')
          for (const line of contentLines) {
            if (line.trim()) {
              // Add each non-empty line for removal
              taskContentsToRemove.push(line)
            }
          }

          // For tasks specifically, also add alternative formats for robustness
          if (task.taskInfo.state && task.taskInfo.originalLine) {
            const { bullet, state, content } = task.taskInfo

            // Add format without state markers (for tasks that might be stored without [ ])
            if (state === ' ' || state === '>') {
              taskContentsToRemove.push(`${bullet} ${content}`)
            }
          }
        }

        // Prepare remove operations for the source note (required for move operation)
        const removeOperations: RemoveContentInput[] = [
          {
            type: 'remove',
            dateString: removeExtension(noteKey.filename),
            pattern: taskContentsToRemove,
          },
        ]

        // Execute operations in a single batch to get only one notification

        // Validate that we can perform the move operation
        if (taskContentsToRemove.length === 0 || !noteKey.filename) {
          throw new TaskMoveError(
            'Cannot move tasks: source note information is missing',
            TaskMoveErrorType.VALIDATION_ERROR,
            {
              taskContentsToRemove: taskContentsToRemove.length,
              sourceFilename: noteKey.filename,
            }
          )
        }

        // Execute add operation without notification
        const addResult = addContent(
          addOperations,
          undefined, // No description = no notification
          batchId,
          teamspaceId
        )

        if (!addResult) {
          throw new TaskMoveError(
            'Failed to queue add operation',
            TaskMoveErrorType.PROCESSING_ERROR,
            { targetFilename, taskCount: tasks.length }
          )
        }

        // Execute remove operation (required for move operation)
        const taskCount = tasks.length
        const operationDescription = `Moved ${taskCount.toString()} task${taskCount === 1 ? '' : 's'}! 🎯`

        const removeResult = removeContent(
          removeOperations,
          operationDescription, // Use description for final notification
          teamspaceId
        )

        if (!removeResult) {
          throw new TaskMoveError(
            'Failed to queue remove operation for task move',
            TaskMoveErrorType.PROCESSING_ERROR,
            {
              removeOperationsCount: removeOperations.length,
              sourceFilename: noteKey.filename,
            }
          )
        }

        // NoteProcessorProvider will handle notification for remove operations

        // Track successful queuing (not completion)
        trackEvent('web.useTaskMove.queued', {
          taskCount: tasks.length,
          targetFilename,
          batchId,
        })

        return {
          success: true,
          batchId,
        }
      } catch (error) {
        let taskMoveError: TaskMoveError

        if (error instanceof TaskMoveError) {
          taskMoveError = error
        } else {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error'
          taskMoveError = new TaskMoveError(
            errorMessage,
            TaskMoveErrorType.PROCESSING_ERROR,
            {
              originalError:
                error instanceof Error ? error : new Error(String(error)),
            }
          )
        }

        // Track the error with enhanced details
        trackError('useTaskMove', 'Task move operation failed', {
          error: taskMoveError.message,
          errorType: taskMoveError.type,
          taskCount: tasks.length || 0,
          targetDate,
          details: taskMoveError.details,
        })

        // Show contextual error notification based on error type
        const notificationConfig = getErrorNotificationConfig(taskMoveError)
        showNotification(notificationConfig)

        return {
          success: false,
          error: taskMoveError,
        }
      }
    },
    [addContent, removeContent, noteKey.filename, showNotification]
  )

  return {
    moveTaskToDate,
  }
}

/**
 * Gets appropriate notification configuration based on error type
 */
function getErrorNotificationConfig(error: TaskMoveError) {
  switch (error.type) {
    case TaskMoveErrorType.VALIDATION_ERROR: {
      return {
        title: 'Invalid Operation',
        message: `Cannot move tasks: ${error.message}`,
        icon: 'fa-exclamation-circle text-yellow-500',
      }
    }

    case TaskMoveErrorType.EXTRACTION_ERROR: {
      return {
        title: 'Extraction Failed',
        message: `Could not extract task data: ${error.message}`,
        icon: 'fa-file-exclamation text-orange-500',
      }
    }

    case TaskMoveErrorType.NETWORK_ERROR: {
      return {
        title: 'Network Error',
        message: `Failed to save changes: ${error.message}`,
        icon: 'fa-wifi text-red-500',
      }
    }

    case TaskMoveErrorType.PROCESSING_ERROR:
    default: {
      return {
        title: 'Move Failed',
        message: `Failed to move tasks: ${error.message}`,
        icon: errorIcon,
      }
    }
  }
}
